import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  DisplayProps,
  JustifyContentProps,
  MinHeightProps,
  OverflowProps,
  PositionProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  display,
  justifyContent,
  minHeight,
  overflow,
  position,
  space,
  width,
  zIndex,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { PrismicImage } from 'common/components/PrismicResponsiveImage';
import PrismicResponsiveImage from 'common/components/PrismicResponsiveImage';
import { Code } from 'common/components/Voucher';
import CtaButtonWithDrawer from 'common/components/Voucher/CtaButtonWithDrawer';
import type { Color } from 'common/theme/colors/types';
import { getForwardPropOpts } from 'common/theme/helpers/forward-props';
import type { SpaceProps } from 'common/theme/system-types';
import type {
  BoxSizingProps,
  ThemeBottomProps,
  ThemeTopProps,
} from 'common/theme/system-utilities';
import { boxSizing, themeBottom, themeTop } from 'common/theme/system-utilities';
import TopBannerContentWrapper from './TopBannerContentWrapper';
import type { BannerProps } from './TopTextBanner';
import {
  codeStyles,
  countDownStyles,
  legalDisclaimerStyles,
  StyledCountDown,
  StyledLegalDisclaimer,
  SubTitle,
  subTitleStyles,
  Title,
  titleStyles,
} from './TopTextBanner';

interface TopImageBannerProps extends BannerProps {
  image: PrismicImage;
}

const UnstyledBannerImageContainer = styled(Flex)<PositionProps & OverflowProps>`
  ${position};
  ${overflow};
  float: left;
`;

const BannerImageContainer = (props: ComponentProps<typeof UnstyledBannerImageContainer>) => (
  <UnstyledBannerImageContainer
    justifyContent={[null, null, 'center']}
    position="relative"
    flexDirection="column"
    width="100%"
    overflow="hidden"
    {...props}
  />
);

const UnstyledImageWithLegalContainer = styled.div<PositionProps>`
  ${position};
`;

const ImageWithLegalContainer = (props: ComponentProps<typeof UnstyledImageWithLegalContainer>) => (
  <UnstyledImageWithLegalContainer position="relative" {...props} />
);

const UnstyledStyledPrismicImage = styled(
  PrismicResponsiveImage,
  getForwardPropOpts(['justifyContent'], false)
)<JustifyContentProps & WidthProps & MinHeightProps>`
  ${justifyContent};
  ${width};
  ${minHeight};
`;

const StyledPrismicImage = (props: ComponentProps<typeof UnstyledStyledPrismicImage>) => (
  <UnstyledStyledPrismicImage minHeight={['100px', '150px', '250px', '350px']} {...props} />
);

const UnstyledBlockWrapper = styled(Flex)<
  PositionProps & ThemeTopProps & ThemeBottomProps & ZIndexProps
>`
  ${position};
  ${themeTop};
  ${themeBottom};
  ${zIndex};
`;

const BlockWrapper = (props: ComponentProps<typeof UnstyledBlockWrapper>) => (
  <UnstyledBlockWrapper
    alignItems="center"
    position={[null, null, 'absolute']}
    px={['sp_24', null, '52px', 'sp_64']}
    mt={['-8px', '-24px', 0]}
    pt={[null, null, 'sp_20', 'sp_24']}
    maxWidth={[null, null, '380px', '508px']}
    themeTop="sp_0"
    themeBottom="sp_0"
    zIndex={1}
    {...props}
  />
);

const UnstyledContentWrapper = styled(TopBannerContentWrapper)<
  DisplayProps & SpaceProps & BoxSizingProps
>`
  ${display};
  ${space};
  ${boxSizing};
`;

const ContentWrapper = (props: ComponentProps<typeof UnstyledContentWrapper>) => (
  <UnstyledContentWrapper
    display="block"
    p={['sp_8', 'sp_16', 'sp_20', 'sp_24']}
    boxSizing="border-box"
    {...props}
  />
);

const imageTitleStyles: ComponentProps<typeof Title> = {
  ...titleStyles,
  mt: 'sp_0',
  mb: ['sp_4', 'sp_8'],
  mx: 'sp_0',
  width: 1,
};

const imageSubTitleStyles: ComponentProps<typeof SubTitle> = {
  ...subTitleStyles,
  mb: 'sp_0',
  width: 1,
};

const footerStyles: ComponentProps<typeof Flex> = {
  flexDirection: ['column', 'row', 'column'],
  justifyContent: ['center', 'space-around', 'center'],
  alignItems: ['center', null, 'flex-start'],
  mt: ['sp_0', 'sp_8', 'sp_12', 'sp_16'],
  width: 1,
};

const imageCountDownStyles: Partial<ComponentProps<typeof StyledCountDown>> = {
  ...countDownStyles,
  my: 'sp_4',
};

const UnstyledStyledTopBannerCtaButton = styled(CtaButtonWithDrawer)<SpaceProps>`
  ${space};
`;

const StyledTopBannerCtaButton = (
  props: ComponentProps<typeof UnstyledStyledTopBannerCtaButton>
) => <UnstyledStyledTopBannerCtaButton my="sp_4" {...props} />;

// eslint-disable-next-line complexity
const TopImageBanner = (props: TopImageBannerProps) => {
  const {
    bannerStartBgColor,
    bannerEndBgColor,
    legalDisclaimerContent,
    legalDisclaimerLinkLabel,
    legalDisclaimerLabelColor,
    countdownEndDate,
    countdownLabel,
    countdownColor,
    titleColor,
    subTitleColor,
    title,
    subTitle,
    voucherCode,
    voucherCodeLabel,
    voucherCodeTextColor,
    voucherCodeBoxBgColor,
    image,
    ctaLabel,
    ctaColor,
    ctaDisclaimerText,
    onPromoClick,
    onPromoView,
  } = props;

  const hasVoucher = title || subTitle || voucherCode || countdownEndDate || ctaLabel;
  const hasDisclaimer = legalDisclaimerContent && legalDisclaimerLinkLabel;
  const hasCtaButton = !voucherCode && ctaLabel && ctaDisclaimerText;

  return (
    <BannerImageContainer>
      <ImageWithLegalContainer>
        <StyledPrismicImage
          image={image}
          highPriority
          data-testid="top-banner-image"
          justifyContent="center"
          width="100%"
        />

        {hasDisclaimer && (
          <StyledLegalDisclaimer
            {...legalDisclaimerStyles}
            color={legalDisclaimerLabelColor || 'text'}
            content={legalDisclaimerContent}
            label={legalDisclaimerLinkLabel}
          />
        )}
      </ImageWithLegalContainer>
      {hasVoucher && (
        <BlockWrapper>
          <ContentWrapper
            onView={onPromoView}
            startColor={bannerStartBgColor}
            endColor={bannerEndBgColor}
          >
            {title && (
              <Title {...imageTitleStyles} color={titleColor as Color}>
                {title}
              </Title>
            )}
            {subTitle && (
              <SubTitle {...imageSubTitleStyles} color={subTitleColor as Color}>
                {subTitle}
              </SubTitle>
            )}
            <Flex {...footerStyles}>
              {countdownEndDate && (
                <StyledCountDown
                  {...imageCountDownStyles}
                  endDate={countdownEndDate}
                  label={countdownLabel}
                  color={countdownColor as Color}
                />
              )}
              <Code
                {...codeStyles}
                onClick={onPromoClick}
                label={voucherCodeLabel}
                code={voucherCode}
                bgColor={voucherCodeBoxBgColor}
                textColor={voucherCodeTextColor}
              />
              {hasCtaButton && (
                <StyledTopBannerCtaButton
                  ctaLabel={ctaLabel}
                  ctaColor={ctaColor}
                  ctaDisclaimerText={ctaDisclaimerText}
                />
              )}
            </Flex>
          </ContentWrapper>
        </BlockWrapper>
      )}
    </BannerImageContainer>
  );
};

export default TopImageBanner;
